"use client"

import { usePathname, useSearchParams } from "next/navigation"
import { useEffect } from "react"

/**
 * This site uses Next.js client-side routing (next/link + output: 'export'),
 * so after the very first page load, navigating between pages does NOT
 * trigger a new browser page load — which means GTM/GA4's automatic
 * page_view (which only fires once, on initial load) never fires again.
 *
 * This component pushes a virtual pageview event into the GTM dataLayer
 * every time the route changes, so GTM can pick it up and send a fresh
 * page_view to GA4 for every page the user visits.
 *
 * NOTE: In GTM, make sure there's a trigger listening for the custom event
 * "virtualPageview" (Custom Event trigger, event name = virtualPageview)
 * hooked up to the GA4 Configuration/Event tag. Without that trigger, this
 * push alone won't do anything — GTM has to be told to listen for it.
 */
export default function GTMRouteTracker() {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  useEffect(() => {
    if (typeof window === "undefined") return

    const query = searchParams.toString()
    const url = query ? `${pathname}?${query}` : pathname

    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: "virtualPageview",
      page_path: url,
    })
  }, [pathname, searchParams])

  return null
}

declare global {
  interface Window {
    dataLayer: any[]
  }
}
